import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Shield, RefreshCw, X, ArrowRight, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { createClient } from '@supabase/supabase-js';
import bcrypt from 'bcryptjs';
import { registerUser } from '../../services/supabaseService';

const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY,
  import.meta.env.VITE_LOCALHOST_URL
);

const VerificationModal = ({ isOpen, onClose, email, formData, onSuccess }) => {
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(300); // 5 dakika
  const [canResend, setCanResend] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(30); // 30 saniye bekleme süresi
  const [isResendActive, setIsResendActive] = useState(false);
  const [wrongAttempts, setWrongAttempts] = useState(0); // Yanlış deneme sayısı ekle
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(true);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (!isOpen) return;

    // Ana zamanlayıcı (5 dakika)
    const timer = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          setCanResend(true);
          clearInterval(timer);
          // Süre bittiğinde kodu geçersiz yap
          handleExpireCode();
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    // Yeniden gönderme için zamanlayıcı (30 saniye) - sadece başlangıçta çalışsın
    let cooldownTimer;
    if (resendCooldown > 0) { // Sadece süre varsa çalışsın
      cooldownTimer = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(cooldownTimer);
            setCanResend(true);
            setIsResendActive(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      clearInterval(timer);
      if (cooldownTimer) clearInterval(cooldownTimer);
    };
  }, [isOpen, resendCooldown]);

  // Scroll yönetimi için useEffect
  useEffect(() => {
    if (isOpen) {
      // Modalı açarken sayfayı en yukarı kaydır
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      // Body scroll'u devre dışı bırak
      document.body.style.overflow = 'hidden';
      
      // Modal kapandığında body scroll'u tekrar etkinleştir
      return () => {
        document.body.style.overflow = 'unset';
      };
    }
  }, [isOpen]);

  // Süre bittiğinde kodu geçersiz yapma fonksiyonu
  const handleExpireCode = async () => {
    try {
      await supabase
        .from('verification_codes')
        .update({ used: true })
        .eq('email', email)
        .eq('used', false);
    } catch (error) {
      console.error('Code expiration error:', error);
    }
  };

  // Modal kapatma işleyicisi
  const handleClose = async () => {
    try {
      // Mevcut aktif kodu geçersiz yap
      await supabase
        .from('verification_codes')
        .update({ used: true })
        .eq('email', email)
        .eq('used', false);

      // Tüm state'leri sıfırla
      setVerificationCode(['', '', '', '', '', '']); // Input alanlarını temizle
      setTimeLeft(300); // 5 dakika süreyi sıfırla
      setCanResend(false);
      setIsResendActive(false);
      setWrongAttempts(0); // Yanlış deneme sayısını sıfırla
      setResendCooldown(30); // Yeni kod gönderme süresini sıfırla

      // Modalı kapat
      onClose();
    } catch (error) {
      console.error('Close modal error:', error);
      onClose(); // Hata olsa bile modalı kapat
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleCodeChange = (index, value) => {
    if (value.length > 1) value = value[0];
    if (!/^[0-9]*$/.test(value)) return;

    const newCode = [...verificationCode];
    newCode[index] = value;
    setVerificationCode(newCode);

    // Otomatik olarak sonraki input'a geç
    if (value !== '' && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && verificationCode[index] === '' && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    if (!/^\d+$/.test(pastedData)) return;

    const newCode = [...verificationCode];
    pastedData.split('').forEach((char, index) => {
      if (index < 6) newCode[index] = char;
    });
    setVerificationCode(newCode);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // 3 yanlış deneme kontrolü
      if (wrongAttempts >= 3) {
        toast.error('Maksimum deneme sayısına ulaştınız. İşlem iptal edildi.', {
          duration: 5000,
          icon: '🚫'
        });
        handleClose();
        return;
      }

      const code = verificationCode.join('');
      
      // Doğrulama kodunu kontrol et
      const { data: verificationData, error: verificationError } = await supabase
        .from('verification_codes')
        .select('*')
        .eq('email', email)
        .eq('code', code)
        .eq('used', false)
        .gt('expires_at', new Date().toISOString())
        .single();

      if (verificationError || !verificationData) {
        // Yanlış deneme sayısını artır
        const newAttempts = wrongAttempts + 1;
        setWrongAttempts(newAttempts);

        // Input alanlarını temizle
        setVerificationCode(['', '', '', '', '', '']);
        
        // İlk input'a odaklan
        if (inputRefs.current[0]) {
          inputRefs.current[0].focus();
        }

        // 3 yanlış deneme kontrolü
        if (newAttempts >= 3) {
          // Mevcut aktif kodu geçersiz yap
          await supabase
            .from('verification_codes')
            .update({ used: true })
            .eq('email', email)
            .eq('used', false);

          toast.error('3 kez yanlış kod girdiniz. İşlem iptal edildi.', {
            duration: 5000,
            icon: '🚫'
          });
          
          // Modalı kapat
          handleClose();
          return;
        }

        // Kalan deneme hakkını göster
        toast.error(`Geçersiz kod. ${3 - newAttempts} deneme hakkınız kaldı.`, {
          duration: 3000,
          icon: '⚠️'
        });
        
        setIsLoading(false);
        return;
      }

      // Doğru kod girildiğinde yanlış deneme sayısını sıfırla
      setWrongAttempts(0);

      // Profil fotoğrafını yükle
      let profilePhotoUrl = null;
      if (formData.profilePhoto) {
        const fileExt = formData.profilePhoto.name.split('.').pop();
        const fileName = `${Date.now()}.${fileExt}`;
        const filePath = `profile-photos/${fileName}`;

        const { error: uploadError, data } = await supabase.storage
          .from('profile-photos')
          .upload(filePath, formData.profilePhoto);

        if (uploadError) {
          console.error('Profile photo upload error:', uploadError);
        } else {
          const { data: { publicUrl } } = supabase.storage
            .from('profile-photos')
            .getPublicUrl(filePath);
          profilePhotoUrl = publicUrl;
        }
      }

      // Şifreyi hashle
      const salt = await bcrypt.genSalt(10);
      const hashedPassword = await bcrypt.hash(formData.password, salt);

      // Auth kullanıcısını oluştur
      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password
      });

      if (signUpError) {
        throw signUpError;
      }

      if (!authData.user?.id) {
        throw new Error('Kullanıcı ID\'si alınamadı');
      }

      // Profili oluştur
      const { error: insertError } = await supabase
        .from('user_profiles')
        .insert([{
          id: authData.user.id,
          email: formData.email,
          first_name: formData.firstName,
          last_name: formData.lastName,
          phone: formData.phone,
          country: formData.country,
          city: formData.city,
          national_id: formData.tcNo,
          password_hash: hashedPassword,
          birth_date: formData.birthDate,
          gender: formData.gender,
          job_position: formData.position,
          website: formData.website,
          address: formData.address,
          profile_photo_url: profilePhotoUrl,
          accepted_terms: formData.termsAccepted,
          accepted_kvkk: formData.kvkkAccepted,
          subscribed_newsletter: formData.newsletterSubscription,
          accepted_data_processing: formData.dataShareConsent,
          online_status: true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }])

      if (insertError) {
        // Profil fotoğrafını sil
        if (profilePhotoUrl) {
          await supabase.storage
            .from('profile-photos')
            .remove([profilePhotoUrl.split('/').pop()]);
        }
        console.error('Profile update error:', insertError);
        throw new Error('Profil bilgileri güncellenirken bir hata oluştu: ' + insertError.message);
      }

      // Doğrulama kodunu kullanıldı olarak işaretle
      await supabase
        .from('verification_codes')
        .update({ used: true })
        .eq('id', verificationData.id);

      // Önce doğrulama modalını kapat
      setShowVerificationModal(false);
      
      // Kısa bir gecikme ile başarı modalını göster
      setTimeout(() => {
        setShowSuccessModal(true);
      }, 800);

      // 7 saniye sonra giriş sayfasına yönlendir
      setTimeout(() => {
        onSuccess();
      }, 7500);

    } catch (error) {
      console.error('Verification error:', error);
      toast.error(error.message || 'Bir hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!isResendActive) return;

    setIsLoading(true);
    try {
      // Tüm state'leri sıfırla
      setVerificationCode(['', '', '', '', '', '']); // Input alanlarını temizle
      setTimeLeft(300); // 5 dakika süreyi sıfırla
      setWrongAttempts(0); // Yanlış deneme sayısını sıfırla
      setCanResend(false);
      setIsResendActive(false);
      setResendCooldown(0); // Yeni kod gönderme süresini sıfırla - artık saymasın

      // Yeni kod oluştur
      const newCode = Math.floor(100000 + Math.random() * 900000).toString();
      const expiresAt = new Date();
      expiresAt.setMinutes(expiresAt.getMinutes() + 5);

      // Cihaz ve bağlantı bilgilerini topla
      const deviceInfo = {
        local_ip_address: await fetch('https://api.ipify.org?format=json').then(res => res.json()).then(data => data.ip),
        ip_address: await fetch('https://api.ipify.org?format=json').then(res => res.json()).then(data => data.ip),
        device_name: navigator.userAgent.match(/\(([^)]+)\)/)[1],
        device_type: /Mobile|iP(hone|od|ad)|Android|BlackBerry|IEMobile/.test(navigator.userAgent) ? 'Mobile' : 'Desktop',
        creation_date: new Date().toISOString(),
        country: 'Unknown',
        address: 'Unknown',
        web_browser_name: navigator.userAgent.match(/(chrome|safari|firefox|msie|trident|edg)/i)?.[0] || 'Unknown',
        screen_resolution: `${window.screen.width}x${window.screen.height}`,
        network_type: navigator.connection?.type || 'Unknown',
        isp_name: 'Unknown',
        browser_version: navigator.userAgent.match(/(?:chrome|safari|firefox|msie|trident|edg)\/?\s*(\d+)/i)?.[1] || 'Unknown',
        browser_language: navigator.language || 'Unknown',
        user_agent: navigator.userAgent,
        time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        screen_orientation: screen.orientation?.type || window.orientation === 0 ? 'portrait' : 'landscape'
      };

      // IP bilgilerini al
      try {
        const ipResponse = await fetch('http://ip-api.com/json');
        const ipData = await ipResponse.json();
        
        if (ipData.status === 'success') {
          deviceInfo.country = ipData.countryCode;
          deviceInfo.address = `${ipData.city}, ${ipData.regionName}, ${ipData.country}`;
          deviceInfo.isp_name = ipData.isp;
          deviceInfo.time_zone = ipData.timezone;
        }
      } catch (error) {
        console.error('IP bilgileri alınamadı:', error);
      }

      // Mevcut aktif kodları geçersiz yap
      await supabase
        .from('verification_codes')
        .update({ used: true })
        .eq('email', email)
        .eq('used', false);

      // Yeni kod oluştur
      const { error } = await supabase
        .from('verification_codes')
        .insert([{
          email,
          code: newCode,
          expires_at: expiresAt.toISOString(),
          device_info: deviceInfo
        }]);

      if (error) throw error;

      // Email gönder
      const response = await fetch(`${import.meta.env.VITE_LOCALHOST_URL}/api/send-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          to: email,
          subject: 'Yeni Email Doğrulama Kodunuz',
          html: `
            <!DOCTYPE html>
            <html>
            <head>
              <meta charset="UTF-8">
              <meta name="viewport" content="width=device-width, initial-scale=1.0">
              <title>Email Doğrulama</title>
              <style>
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
              </style>
            </head>
            <body style="
              margin: 0;
              padding: 0;
              font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              background: linear-gradient(135deg, #f8fafc, #f1f5f9);
              color: #0f172a;
              line-height: 1.5;
            ">
              <table width="100%" cellpadding="0" cellspacing="0" role="presentation" style="margin: 0; padding: 32px 12px;">
                <tr>
                  <td align="center">
                    <table width="100%" cellpadding="0" cellspacing="0" role="presentation" style="max-width: 600px; margin: 0 auto;">
                      <!-- Logo ve Header -->
                      <tr>
                        <td align="center" style="padding: 24px 0;">
                          <img src="https://raw.githubusercontent.com/lucide-icons/lucide/main/icons/shield-check.svg" 
                               width="48" height="48" alt="Logo" style="display: block; margin: 0 auto;">
                          <h1 style="
                            margin: 16px 0 0;
                            font-size: 24px;
                            font-weight: 700;
                            color: #0f172a;
                            text-align: center;
                          ">Beetrax.net - Email Doğrulama</h1>
                        </td>
                      </tr>

                      <!-- Karşılama Mesajı -->
                      <tr>
                        <td style="padding: 0 0 24px 0; text-align: center;">
                          <p style="
                            margin: 0;
                            font-size: 18px;
                            color: #334155;
                          ">Merhaba, <strong>${formData.firstName} ${formData.lastName}</strong></p>
                        </td>
                      </tr>

                      <!-- Ana İçerik -->
                      <tr>
                        <td style="padding: 24px; background: white; border-radius: 12px; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.05);">
                          <p style="
                            margin: 0 0 24px;
                            color: #475569;
                            font-size: 16px;
                            line-height: 1.6;
                          ">
                            Beetrax.net hesabınızı doğrulamak için aşağıdaki kodu kullanın:
                          </p>

                          <!-- Doğrulama Kodu -->
                          <div style="
                            background: #f8fafc;
                            border: 1px solid #e2e8f0;
                            border-radius: 8px;
                            padding: 24px;
                            text-align: center;
                            margin: 24px 0;
                          ">
                            <p style="
                              margin: 0 0 8px;
                              color: #64748b;
                              font-size: 14px;
                            ">Doğrulama Kodunuz</p>
                            <p style="
                              margin: 0;
                              color: #0f172a;
                              font-size: 32px;
                              font-weight: 600;
                              letter-spacing: 4px;
                            ">${newCode}</p>
                          </div>

                          <!-- Güvenlik Bilgileri -->
                          <div style="
                            background: #eff6ff;
                            border-left: 4px solid #3b82f6;
                            border-radius: 4px;
                            padding: 16px;
                            margin: 24px 0;
                          ">
                            <p style="
                              margin: 0;
                              color: #0369a1;
                              font-size: 14px;
                              font-weight: 500;
                            ">Önemli Güvenlik Bilgileri:</p>
                            <ul style="
                              margin: 8px 0 0;
                              padding-left: 20px;
                              color: #0c4a6e;
                              font-size: 14px;
                              line-height: 1.6;
                            ">
                              <li>Bu kod 5 dakika süreyle geçerlidir</li>
                              <li>Kodu kimseyle paylaşmayın</li>
                              <li>Yalnızca bir kez kullanılabilir</li>
                              <li>3 yanlış denemeden sonra kod geçersiz olur</li>
                              <li>Bu işlemi siz yapmadıysanız, lütfen dikkate almayın</li>
                            </ul>
                          </div>

                          <!-- Yardım Metni -->
                          <div style="text-align: center; margin-top: 24px;">
                            <p style="
                              margin: 0;
                              color: #64748b;
                              font-size: 14px;
                            ">Sorun mu yaşıyorsunuz?</p>
                            <p style="
                              margin: 4px 0 0;
                              color: #3b82f6;
                              font-size: 14px;
                            ">Beetrax.net destek ekibimizle iletişime geçin</p>
                          </div>
                        </td>
                      </tr>

                      <!-- Footer -->
                      <tr>
                        <td style="padding: 24px 0; text-align: center;">
                          <p style="
                            margin: 0;
                            color: #64748b;
                            font-size: 14px;
                          ">© ${new Date().getFullYear()} Beetrax.net. Tüm hakları saklıdır.</p>
                          <p style="
                            margin: 8px 0 0;
                            color: #94a3b8;
                            font-size: 12px;
                          ">Bu e-posta Beetrax.net tarafından otomatik olarak gönderilmiştir, lütfen yanıtlamayınız.</p>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
              </table>
            </body>
            </html>
          `
        })
      });

      if (!response.ok) {
        throw new Error('Email gönderilemedi');
      }

      // Başarı mesajı göster
      toast.success('Yeni doğrulama kodu gönderildi. Başka kod gönderme hakkınız kalmadı.', {
        duration: 4000,
        icon: '✉️'
      });

      // İlk input'a odaklan
      if (inputRefs.current[0]) {
        inputRefs.current[0].focus();
      }

    } catch (error) {
      // Hata durumunda state'leri eski haline getir
      setCanResend(true);
      setIsResendActive(true);
      console.error('Resend error:', error);
      toast.error(error.message || 'Kod gönderilemedi');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={handleClose}
        />

        {/* Verification Modal */}
        {showVerificationModal && (
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="relative w-full max-w-lg bg-black/30 dark:bg-black/80 backdrop-blur-xl border border-white/10 dark:border-gray-700/50 rounded-2xl shadow-2xl overflow-hidden"
          >
            {/* Close Button */}
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/10 transition-colors"
            >
              <X className="w-5 h-5 text-white" />
            </button>

            {/* Header */}
            <div className="p-8 text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", duration: 0.6 }}
                className="w-20 h-20 mx-auto mb-6 rounded-full bg-blue-500/20 flex items-center justify-center"
              >
                <Shield className="w-10 h-10 text-blue-400" />
              </motion.div>

              <h2 className="text-2xl font-bold text-white mb-2">
                Email Doğrulama
              </h2>
              <p className="text-gray-200 flex items-center justify-center gap-2 flex-wrap">
                <Mail className="w-4 h-4" />
                {email}
              </p>
            </div>

            {/* Code Input */}
            <form onSubmit={handleSubmit} className="p-8 pt-0">
              <div className="flex justify-center gap-3 mb-8">
                {verificationCode.map((digit, index) => (
                  <motion.input
                    key={index}
                    ref={el => inputRefs.current[index] = el}
                    type="text"
                    maxLength="1"
                    value={digit}
                    onChange={(e) => handleCodeChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={handlePaste}
                    className="w-12 h-16 text-center text-2xl font-bold bg-white/10 border-2 border-white/20 dark:border-gray-700 rounded-xl focus:outline-none focus:border-blue-500 text-white transition-all"
                    initial={{ scale: 0.5, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: index * 0.1 }}
                    required
                  />
                ))}
              </div>

              {/* Timer */}
              <div className="text-center mb-6">
                <p className="text-gray-200 text-sm">
                  Kalan süre: {formatTime(timeLeft)}
                </p>
                {!isResendActive && resendCooldown > 0 ? (
                  <p className="text-blue-400 text-xs mt-1">
                    Yeni kod gönderme: {resendCooldown}s
                  </p>
                ) : !isResendActive && resendCooldown === 0 ? (
                  <p className="text-red-400 text-xs mt-1">
                    Yeniden kod gönderme hakkınız bitmiştir!
                  </p>
                ) : null}
              </div>

              {/* Buttons */}
              <div className="flex flex-col gap-3">
                <motion.button
                  type="submit"
                  disabled={isLoading || verificationCode.some(digit => digit === '')}
                  className={`w-full py-3 px-4 rounded-xl font-medium text-white 
                    ${isLoading || verificationCode.some(digit => digit === '')
                      ? 'bg-gray-600/50 cursor-not-allowed'
                      : 'bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600'
                    } transition-all shadow-lg backdrop-blur-sm flex items-center justify-center gap-2`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {isLoading ? (
                    <>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      >
                        <RefreshCw className="w-5 h-5" />
                      </motion.div>
                      Doğrulanıyor...
                    </>
                  ) : (
                    <>
                      Doğrula
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </motion.button>

                <div className="flex gap-3">
                  <motion.button
                    type="button"
                    onClick={handleResendCode}
                    disabled={isLoading || !canResend || !isResendActive}
                    className={`flex-1 py-3 px-4 rounded-xl font-medium border-2 backdrop-blur-sm
                      ${isLoading || !canResend || !isResendActive
                        ? 'border-gray-700 text-gray-500 cursor-not-allowed'
                        : 'border-blue-500/50 text-blue-400 hover:bg-blue-500/10'
                      } transition-all`}
                    whileHover={{ scale: isResendActive ? 1.02 : 1 }}
                    whileTap={{ scale: isResendActive ? 0.98 : 1 }}
                  >
                    <span className="flex items-center justify-center gap-2">
                      <RefreshCw className="w-5 h-5" />
                      Yeni Kod Gönder
                    </span>
                  </motion.button>

                  <motion.button
                    type="button"
                    onClick={handleClose}
                    className="flex-1 py-3 px-4 rounded-xl font-medium border-2 border-red-500/50 text-red-400 hover:bg-red-500/10 backdrop-blur-sm transition-all"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Vazgeç
                  </motion.button>
                </div>
              </div>
            </form>

            {/* Info Box */}
            <div className="p-8 pt-0">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white/5 dark:bg-gray-800/30 backdrop-blur-sm border border-white/10 dark:border-gray-700 rounded-xl p-4"
              >
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-yellow-500 mt-0.5" />
                  <div className="text-sm text-gray-200">
                    <p className="font-medium text-yellow-500 mb-1">Önemli Bilgi</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Doğrulama kodu 5 dakika süreyle geçerlidir</li>
                      <li>Yeni kod isteme 30 saniye sonra aktif olur</li>
                      <li>Kodu 3 kez yanlış girerseniz işlem iptal edilir</li>
                    </ul>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}

        {/* Success Modal */}
        {showSuccessModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center"
          >
            {/* Arkadaki yarı saydam karartma */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            />

            {/* Modal İçeriği */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="relative px-8 py-6 rounded-2xl shadow-2xl bg-black/30 border border-gray-500 text-white max-w-md w-full mx-4"
            >
              {/* Başarı ikonu */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", duration: 0.5 }}
                className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center"
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.4, type: "spring" }}
                >
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </motion.div>
              </motion.div>

              {/* Başarı mesajı */}
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="text-center"
              >
                <h3 className="text-xl font-semibold mb-2 text-gray-100">
                  Hesabınız Başarıyla Oluşturuldu!
                </h3>
                <motion.p
                  className="text-gray-300"
                  animate={{
                    opacity: [1, 0.5, 1],
                  }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  Giriş sayfasına yönlendiriliyorsunuz
                  <motion.span
                    animate={{
                      opacity: [0, 1, 0],
                    }}
                    transition={{
                      duration: 1,
                      repeat: Infinity,
                      times: [0, 0.33, 1],
                    }}
                  >
                    .
                  </motion.span>
                  <motion.span
                    animate={{
                      opacity: [0, 1, 0],
                    }}
                    transition={{
                      duration: 1,
                      repeat: Infinity,
                      times: [0, 0.33, 1],
                      delay: 0.2,
                    }}
                  >
                    .
                  </motion.span>
                  <motion.span
                    animate={{
                      opacity: [0, 1, 0],
                    }}
                    transition={{
                      duration: 1,
                      repeat: Infinity,
                      times: [0, 0.33, 1],
                      delay: 0.4,
                    }}
                  >
                    .
                  </motion.span>
                </motion.p>
              </motion.div>

              {/* Basit bir yüklenme animasyonu (noktalı) */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="mt-4 flex justify-center"
              >
                <div className="flex space-x-1">
                  <motion.div
                    animate={{
                      scale: [1, 1.2, 1],
                      opacity: [1, 0.8, 1],
                    }}
                    transition={{
                      duration: 1,
                      repeat: Infinity,
                      delay: 0,
                    }}
                    className="w-2 h-2 rounded-full bg-blue-100"
                  />
                  <motion.div
                    animate={{
                      scale: [1, 1.2, 1],
                      opacity: [1, 0.8, 1],
                    }}
                    transition={{
                      duration: 1,
                      repeat: Infinity,
                      delay: 0.2,
                    }}
                    className="w-2 h-2 rounded-full bg-blue-300"
                  />
                  <motion.div
                    animate={{
                      scale: [1, 1.2, 1],
                      opacity: [1, 0.8, 1],
                    }}
                    transition={{
                      duration: 1,
                      repeat: Infinity,
                      delay: 0.4,
                    }}
                    className="w-2 h-2 rounded-full bg-blue-400"
                  />
                </div>
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </div>
    </AnimatePresence>
  );
};

export default VerificationModal; 